/**
 * The optimisation fix: a typed `proposed_fix` and its validator (plan-032 DOM-OR, design §43.6.1, DD32-4, OD-3).
 *
 * A review reads strangers' words, and a model reading them can be steered. So a fix is one of three closed shapes,
 * checked against the REVIEWED document before it is ever stored:
 *
 *   answer        { summary, question, answer }   merged into `answers` when a draft is made (as the gap path does)
 *   config_patch  { summary, configPatch }        an RFC 7386 merge patch over greeting, openingHours, overflow and
 *                                                 afterHours ONLY — never `extraction` (unchecked menus and
 *                                                 questionnaires), never a `{ kind: "transferred" }` arm (a transfer
 *                                                 number set from a stranger's words is a fraud path)
 *   advice        { summary, adviceTarget }       a change the owner makes by hand; never accepted into a draft
 *
 * Rules (design §43.6.1): the kind is one the agent template's rubric may propose (§43.5.3); a `config_patch` changes
 * something and its patched document passes `RuntimeConfigSchema`; an `answer` does not duplicate an existing
 * `answers` entry or an open gap's question, and its document with the answer added passes `RuntimeConfigSchema` too.
 * The TEXT rules (copied caller words, digits, addresses, lengths) are `./finding-text.ts`'s, applied by the run to
 * {@link optimisationFixTextOf}'s texts. A refusal names a code, never the offending text.
 *
 * Stored in `optimisation_finding.proposed_fix` in the existing snake_case wire shape ({@link optimisationFixWireOf}),
 * so the read route's `proposed_fix->>'summary'` keeps working; `optimisation_finding.fix_kind` names which.
 */
import { RuntimeConfigSchema } from "../runtime-config.schema.js";
import { OPTIMISATION_FIX_ANSWER_MAXIMUM, OPTIMISATION_FIX_QUESTION_MAXIMUM, OPTIMISATION_FIX_SUMMARY_MAXIMUM } from "./constant.js";
import { isOptimisationFixKindOfferedBy, OPTIMISATION_FIX_KIND, optimisationReviewRubricOf, } from "./category.js";
/** The top-level `config_document` keys a `config_patch` may change (design §43.6.1). `extraction` is not one. */
export const OPTIMISATION_PATCHABLE_CONFIG_KEY = Object.freeze(["greeting", "openingHours", "overflow", "afterHours"]);
/** Where an `advice` fix sends the owner (the portal links each one to the screen that changes it, design §43.11). */
export const OPTIMISATION_ADVICE_TARGET = Object.freeze([
    "menu",
    "questionnaire",
    "documents",
    "website",
    "voice",
    "transfer_number",
    "other",
]);
export const OPTIMISATION_FIX_REFUSAL = [
    "fix_malformed",
    "fix_kind_not_offered",
    "document_invalid",
    "patch_key_not_allowed",
    "transfer_not_allowed",
    "patch_changes_nothing",
    "answer_duplicate",
    "config_invalid",
];
function isPlainObject(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function boundedText(value, maximum) {
    return typeof value === "string" && value.trim() !== "" && value.length <= maximum;
}
function hasOnlyKeys(value, keys) {
    return Object.keys(value).every((key) => keys.includes(key));
}
/**
 * The comparable form of a question: accents dropped, lower case, anything that is not a letter or a digit read as one
 * space. The same fold the gap path's answer list uses (`apps/api/src/routes/optimisation/publish-answer.ts`).
 */
export function optimisationQuestionKeyOf(question) {
    return question.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();
}
/**
 * RFC 7386 §2 in behaviour. Built with `Object.fromEntries`, so a key spelled `__proto__` is an own key (and then fails
 * `RuntimeConfigSchema`'s `.strict()`), never a prototype assignment. Neither argument is mutated.
 */
export function optimisationMergePatch(target, patch) {
    if (!isPlainObject(patch))
        return patch;
    const merged = new Map(isPlainObject(target) ? Object.entries(target) : []);
    for (const [key, value] of Object.entries(patch)) {
        if (value === null)
            merged.delete(key);
        else
            merged.set(key, optimisationMergePatch(merged.get(key), value));
    }
    return Object.fromEntries(merged);
}
/** Whether any object anywhere inside `value` says `kind: "transferred"` or carries a `transferTo`. */
function carriesTransfer(value) {
    if (Array.isArray(value))
        return value.some(carriesTransfer);
    if (!isPlainObject(value))
        return false;
    if (value["kind"] === "transferred" || Object.hasOwn(value, "transferTo"))
        return true;
    return Object.values(value).some(carriesTransfer);
}
/** A canonical JSON text: object keys sorted, so two equal values compare equal whatever their key order. */
function canonicalOf(value) {
    if (Array.isArray(value))
        return `[${value.map(canonicalOf).join(",")}]`;
    if (isPlainObject(value)) {
        return `{${Object.keys(value)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${canonicalOf(value[key])}`)
            .join(",")}}`;
    }
    return JSON.stringify(value) ?? "null";
}
/** The fix's shape, or `null`. Extra keys are refused: a model that invents a field is not trusted with the rest. */
function shapeOf(fix) {
    if (!isPlainObject(fix))
        return null;
    const kind = fix["kind"];
    if (typeof kind !== "string" || !OPTIMISATION_FIX_KIND.includes(kind))
        return null;
    const summary = fix["summary"];
    if (!boundedText(summary, OPTIMISATION_FIX_SUMMARY_MAXIMUM))
        return null;
    switch (kind) {
        case "answer": {
            const question = fix["question"];
            const answer = fix["answer"];
            if (!hasOnlyKeys(fix, ["kind", "summary", "question", "answer"]))
                return null;
            if (!boundedText(question, OPTIMISATION_FIX_QUESTION_MAXIMUM))
                return null;
            if (!boundedText(answer, OPTIMISATION_FIX_ANSWER_MAXIMUM))
                return null;
            return { kind: "answer", summary: summary.trim(), question: question.trim(), answer: answer.trim() };
        }
        case "config_patch": {
            const configPatch = fix["configPatch"];
            if (!hasOnlyKeys(fix, ["kind", "summary", "configPatch"]))
                return null;
            if (!isPlainObject(configPatch) || Object.keys(configPatch).length === 0)
                return null;
            return { kind: "config_patch", summary: summary.trim(), configPatch };
        }
        case "advice": {
            const adviceTarget = fix["adviceTarget"];
            if (!hasOnlyKeys(fix, ["kind", "summary", "adviceTarget"]))
                return null;
            if (typeof adviceTarget !== "string" || !OPTIMISATION_ADVICE_TARGET.includes(adviceTarget)) {
                return null;
            }
            return { kind: "advice", summary: summary.trim(), adviceTarget: adviceTarget };
        }
    }
}
/**
 * Validates one proposed fix against the reviewed document (design §43.6.1). `fix` is the model's camelCase value,
 * unread; the answer is the typed fix or the first refusal.
 */
export function optimisationFixOf(reviewed, fix) {
    const shaped = shapeOf(fix);
    if (shaped === null)
        return { ok: false, code: "fix_malformed" };
    const document = reviewed.configDocument;
    if (!isPlainObject(document))
        return { ok: false, code: "document_invalid" };
    const agentTemplateCode = document["agentTemplateCode"];
    const rubric = typeof agentTemplateCode === "string" ? optimisationReviewRubricOf(agentTemplateCode) : null;
    if (rubric === null)
        return { ok: false, code: "fix_kind_not_offered" };
    if (!isOptimisationFixKindOfferedBy(rubric, shaped.kind))
        return { ok: false, code: "fix_kind_not_offered" };
    if (shaped.kind === "advice")
        return { ok: true, fix: shaped };
    if (!RuntimeConfigSchema.safeParse(document).success)
        return { ok: false, code: "document_invalid" };
    if (shaped.kind === "answer") {
        const key = optimisationQuestionKeyOf(shaped.question);
        const answers = Array.isArray(document["answers"]) ? document["answers"] : [];
        const said = answers.some((one) => isPlainObject(one) && typeof one["question"] === "string" && optimisationQuestionKeyOf(one["question"]) === key);
        const asked = reviewed.openGapQuestion.some((question) => optimisationQuestionKeyOf(question) === key);
        if (said || asked)
            return { ok: false, code: "answer_duplicate" };
        const withAnswer = { ...document, answers: [...answers, { question: shaped.question, answer: shaped.answer }] };
        if (!RuntimeConfigSchema.safeParse(withAnswer).success)
            return { ok: false, code: "config_invalid" };
        return { ok: true, fix: shaped };
    }
    const patch = shaped.configPatch;
    for (const key of Object.keys(patch)) {
        if (!OPTIMISATION_PATCHABLE_CONFIG_KEY.includes(key)) {
            return { ok: false, code: "patch_key_not_allowed" };
        }
    }
    if (carriesTransfer(patch["overflow"]) || carriesTransfer(patch["afterHours"])) {
        return { ok: false, code: "transfer_not_allowed" };
    }
    const patched = optimisationMergePatch(document, patch);
    for (const key of ["overflow", "afterHours"]) {
        if (Object.hasOwn(patch, key) && carriesTransfer(patched[key]))
            return { ok: false, code: "transfer_not_allowed" };
    }
    const changed = Object.keys(patch).some((key) => canonicalOf(patched[key]) !== canonicalOf(document[key]));
    if (!changed)
        return { ok: false, code: "patch_changes_nothing" };
    if (!RuntimeConfigSchema.safeParse(patched).success)
        return { ok: false, code: "config_invalid" };
    return { ok: true, fix: shaped };
}
/** Every text of a fix the run checks with `optimisationFindingTextProblemOf`, with the field that sets its limit. */
export function optimisationFixTextOf(fix) {
    if (fix.kind === "answer") {
        return [
            { field: "summary", text: fix.summary },
            { field: "question", text: fix.question },
            { field: "answer", text: fix.answer },
        ];
    }
    return [{ field: "summary", text: fix.summary }];
}
/**
 * Design §43.6.1's last rule: an `answer` or `config_patch` still refused after the one repair becomes advice with
 * `adviceTarget: "other"` (when its summary passes the text check — the caller decides that).
 */
export function optimisationFixAsAdviceOf(fix) {
    return { kind: "advice", summary: fix.summary, adviceTarget: fix.kind === "advice" ? fix.adviceTarget : "other" };
}
/** The stored `optimisation_finding.proposed_fix`: snake_case, `summary` always first-level. */
export function optimisationFixWireOf(fix) {
    switch (fix.kind) {
        case "answer":
            return { summary: fix.summary, question: fix.question, answer: fix.answer };
        case "config_patch":
            return { summary: fix.summary, config_patch: fix.configPatch };
        case "advice":
            return { summary: fix.summary, advice_target: fix.adviceTarget };
    }
}
/** Reads a stored `proposed_fix` back under its `fix_kind`, or `null` when the two do not agree. */
export function optimisationFixFromWireOf(fixKind, proposedFix) {
    if (!isPlainObject(proposedFix))
        return null;
    switch (fixKind) {
        case "answer":
            return shapeOf({
                kind: "answer",
                summary: proposedFix["summary"],
                question: proposedFix["question"],
                answer: proposedFix["answer"],
            });
        case "config_patch":
            return shapeOf({ kind: "config_patch", summary: proposedFix["summary"], configPatch: proposedFix["config_patch"] });
        case "advice":
            return shapeOf({ kind: "advice", summary: proposedFix["summary"], adviceTarget: proposedFix["advice_target"] });
        default:
            return null;
    }
}
//# sourceMappingURL=fix.js.map